// src/js/core/auth.js
import { supabaseClient } from "./supabaseClient.js";

// Helper to get the correct site URL
function getSiteUrl() {
  const isProd = window.location.hostname.includes("lunalingo.com");
  return isProd ? "https://www.lunalingo.com" : "http://localhost:8888";
}

// Core auth functions
async function signInWithGoogle() {
  try {
    const { data, error } = await supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getSiteUrl(),
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Error signing in with Google:", error);
    return { data: null, error };
  }
}

async function signOut() {
  try {
    const { error } = await supabaseClient.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error("Error signing out:", error);
    return { error };
  }
}

async function getCurrentUser() {
  try {
    // First check if we have a session
    const {
      data: { session },
    } = await supabaseClient.auth.getSession();

    if (!session) {
      return { user: null, error: null };
    }

    // If we have a session, try getClaims for performance
    try {
      const result = await supabaseClient.auth.getClaims();

      // Handle different possible response formats
      let claims = null;
      if (result?.data?.claims) {
        claims = result.data.claims; // Current version format
      } else if (result?.claims) {
        claims = result.claims;
      } else if (result?.data) {
        claims = result.data;
      } else if (result && typeof result === "object" && result.sub) {
        claims = result; // Direct claims object
      }

      if (claims && claims.role === "authenticated" && !claims.is_anonymous) {
        const user = {
          id: claims.sub,
          email: claims.email,
          user_metadata: claims.user_metadata || {},
        };
        return { user, error: null };
      }
    } catch (claimsError) {
      console.warn("getClaims failed, falling back to getUser:", claimsError);
    }

    // Fallback to getUser if getClaims fails
    const {
      data: { user },
      error,
    } = await supabaseClient.auth.getUser();

    if (error?.name === "AuthSessionMissingError") {
      return { user: null, error: null };
    }

    if (error) throw error;

    return { user, error: null };
  } catch (error) {
    console.error("Error getting current user:", error);
    return { user: null, error };
  }
}

async function checkPremiumStatus(userId) {
  try {
    const { data, error } = await supabaseClient
      .from("subscriptions")
      .select("subscription_status")
      .eq("user_id", userId)
      .maybeSingle(); // Use maybeSingle() instead of single()

    if (error && error.code !== "PGRST116") {
      // PGRST116 means no rows found
      console.error("Error checking premium status:", error);
      return false;
    }

    return data?.subscription_status === "active";
  } catch (error) {
    console.error("Error checking premium status:", error);
    return false;
  }
}

// UI initialization
function initializeAuth() {
  const signInButton = document.querySelector(".auth-button");
  const signOutButton = document.querySelector(".sign-out-button");
  const userAccountContainer = document.querySelector(".user-account-container");
  const limitMessageAuthButton = document.querySelector("#limitMessage-guest .auth-button");
  const initialsButton = document.querySelector(".user-initials");

  // Menu handling
  function closeMenu() {
    userAccountContainer.classList.remove("menu-open");
  }

  function handleInitialsClick(e) {
    e.stopPropagation();
    userAccountContainer.classList.toggle("menu-open");
  }

  // Close menu when clicking outside
  document.addEventListener("click", closeMenu);

  // Close menu on escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeMenu();
  });

  // Stop menu from closing when clicking inside it
  userAccountContainer?.addEventListener("click", (e) => e.stopPropagation());

  // Toggle menu when clicking initials
  initialsButton?.addEventListener("click", handleInitialsClick);

  async function updateAuthUI() {
    const { user } = await getCurrentUser();

    if (user) {
      // User is signed in
      signInButton.style.display = "none";
      userAccountContainer.style.display = "flex";
      document.body.classList.add("authenticated");

      // Get subscription details
      const { data: subscription } = await supabaseClient
        .from("subscriptions")
        .select("subscription_status")
        .eq("user_id", user.id)
        .maybeSingle();

      // Check premium status and update UI
      const isPremium = subscription?.subscription_status === "active";
      document.body.classList.toggle("premium", isPremium);

      // Get and display user initials
      const { name, email } = user.user_metadata;
      if (name) {
        const nameParts = name
          .trim()
          .split(" ")
          .filter((part) => part);
        const firstInitial = nameParts[0]?.[0] || "";
        const lastInitial = nameParts[nameParts.length - 1]?.[0] || "";
        const initials =
          nameParts.length > 1 ? `${firstInitial}${lastInitial}`.toUpperCase() : firstInitial.toUpperCase();
        document.querySelector(".user-initials").textContent = initials;
        document.querySelector(".user-email").textContent = email;
      }
    } else {
      // User is signed out
      signInButton.style.display = "flex";
      userAccountContainer.style.display = "none";
      document.body.classList.remove("authenticated", "premium");
      closeMenu();
    }
  }

  // Attach event listeners to buttons
  signInButton?.addEventListener("click", async () => {
    // Clear storage before Google sign in
    localStorage.removeItem("dialogue_usage_info");
    localStorage.removeItem("tts_usage_info");
    await signInWithGoogle();
  });

  signOutButton?.addEventListener("click", async () => {
    await signOut();
    window.location.reload(); // Reload after signout
  });

  // Handle limit message auth button (if it exists)
  limitMessageAuthButton?.addEventListener("click", async () => {
    // Clear storage before Google sign in
    localStorage.removeItem("dialogue_usage_info");
    localStorage.removeItem("tts_usage_info");
    await signInWithGoogle();
  });

  // Listen for auth state changes
  supabaseClient.auth.onAuthStateChange((event, session) => {
    console.log("Auth state changed:", event);
    updateAuthUI();
  });

  // Initial UI update
  updateAuthUI();
}

export { initializeAuth, getCurrentUser, signInWithGoogle, signOut };
